/**
 * PromoCodeManagement - CRUD for discount codes
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ticket, Percent, DollarSign, Loader2, Copy, CheckCircle } from 'lucide-react';
import { promoAPI, getErrorMessage } from '@/lib/api';

// Form component extracted outside
const PromoForm = ({ promo, onSubmit, onCancel }) => {
  const [form, setForm] = useState(promo || { code: '', description: '', discount_type: 'percentage', discount_value: 10, max_uses: '', min_purchase: '', valid_from: '', valid_until: '', is_active: true });
  const generateCode = () => setForm({...form, code: `SAVE${Math.random().toString(36).substring(2, 8).toUpperCase()}`});
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="flex-1"><Label>Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SUMMER2024" /></div>
        <Button type="button" variant="outline" className="mt-6" onClick={generateCode}>Generate</Button>
      </div>
      <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} placeholder="Summer sale discount" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Discount Type</Label>
          <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage (%)</SelectItem>
              <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Value *</Label>
          <div className="relative">
            <Input type="number" value={form.discount_value} onChange={e => setForm({...form, discount_value: parseFloat(e.target.value) || 0})} className="pl-8" />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{form.discount_type === 'percentage' ? '%' : '$'}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Max Uses (blank=unlimited)</Label><Input type="number" value={form.max_uses || ''} onChange={e => setForm({...form, max_uses: parseInt(e.target.value) || ''})} /></div>
        <div><Label>Min Purchase ($)</Label><Input type="number" value={form.min_purchase || ''} onChange={e => setForm({...form, min_purchase: parseFloat(e.target.value) || ''})} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Valid From</Label><Input type="datetime-local" value={form.valid_from ? new Date(form.valid_from).toISOString().slice(0, 16) : ''} onChange={e => setForm({...form, valid_from: e.target.value})} /></div>
        <div><Label>Valid Until</Label><Input type="datetime-local" value={form.valid_until ? new Date(form.valid_until).toISOString().slice(0, 16) : ''} onChange={e => setForm({...form, valid_until: e.target.value})} /></div>
      </div>
      <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label>Active</Label></div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.code || !form.discount_value}>{promo ? 'Update' : 'Create'}</Button>
      </div>
    </div>
  );
};

const PromoCodeManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [copied, setCopied] = useState(null);

  const { data: promoCodes = [], isLoading } = useQuery({
    queryKey: ['promo-codes', eventId],
    queryFn: () => promoAPI.list(eventId).then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data) => promoAPI.create(eventId, data),
    onSuccess: () => { toast.success('Promo code created'); queryClient.invalidateQueries(['promo-codes', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => promoAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Promo code updated'); queryClient.invalidateQueries(['promo-codes', eventId]); setEditingCode(null); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => promoAPI.delete(eventId, id),
    onSuccess: () => { toast.success('Promo code deleted'); queryClient.invalidateQueries(['promo-codes', eventId]); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const totalUsage = promoCodes.reduce((sum, c) => sum + (c.used_count || 0), 0);
  const activeCodes = promoCodes.filter(c => c.is_active).length;

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="promo-code-management">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Ticket className="h-6 w-6" />Promo Codes</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Create Code</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Create Promo Code</DialogTitle></DialogHeader>
            <PromoForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{promoCodes.length}</p><p className="text-sm text-slate-500">Total Codes</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-green-600">{activeCodes}</p><p className="text-sm text-slate-500">Active</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-blue-600">{totalUsage}</p><p className="text-sm text-slate-500">Total Uses</p></CardContent></Card>
      </div>

      {/* Code List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {promoCodes.map(code => (
          <Card key={code.id} data-testid={`promo-${code.id}`} className={!code.is_active ? 'opacity-60' : ''}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <code className="text-lg font-bold bg-slate-100 px-3 py-1 rounded">{code.code}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyCode(code.code)}>
                    {copied === code.code ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <Badge variant={code.is_active ? 'default' : 'secondary'}>{code.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-lg font-semibold mb-2">
                {code.discount_type === 'percentage' ? <Percent className="h-5 w-5 text-green-600" /> : <DollarSign className="h-5 w-5 text-green-600" />}
                <span>{code.discount_value}{code.discount_type === 'percentage' ? '%' : ''} off</span>
              </div>
              {code.description && <p className="text-sm text-slate-600 mb-2">{code.description}</p>}
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>Used: {code.used_count || 0}{code.max_uses ? `/${code.max_uses}` : ''}</span>
                {code.valid_until && <span>Expires: {new Date(code.valid_until).toLocaleDateString()}</span>}
              </div>
              <div className="flex gap-1 mt-3">
                <Button variant="ghost" size="sm" onClick={() => setEditingCode(code)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(code.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {promoCodes.length === 0 && <p className="col-span-2 text-center text-slate-500 py-8">No promo codes created yet.</p>}
      </div>

      <Dialog open={!!editingCode} onOpenChange={() => setEditingCode(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Promo Code</DialogTitle></DialogHeader>
          {editingCode && <PromoForm promo={editingCode} onSubmit={data => updateMutation.mutate({ id: editingCode.id, data })} onCancel={() => setEditingCode(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodeManagement;
